import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import config from '../utils/envLoader';

const useTokenExpiry = () => {
    const { isAuthenticated, logout, refreshToken } = useAuth();
    const [isWarningVisible, setIsWarningVisible] = useState(false);
    const [timeUntilExpiry, setTimeUntilExpiry] = useState(null);
    const intervalRef = useRef(null);
    const warningShownRef = useRef(false);

    // Helper function to decode JWT token and extract expiry
    const decodeTokenExpiry = useCallback((token) => {
        try {
            if (!token) return null;

            // JWT tokens have 3 parts separated by dots
            const parts = token.split('.');
            if (parts.length !== 3) return null;

            // Decode the payload (second part)
            const payload = JSON.parse(atob(parts[1]));

            // JWT exp is in seconds, convert to milliseconds
            return payload.exp ? payload.exp * 1000 : null;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    }, []);

    // Get token expiry from localStorage
    const getTokenExpiry = useCallback(() => {
        try {
            const token = localStorage.getItem(config.tokenStorageKey);
            if (!token) return null;

            return decodeTokenExpiry(token);
        } catch (error) {
            console.error('Error getting token expiry:', error);
            return null;
        }
    }, [decodeTokenExpiry]);

    // Calculate time until token expires
    const calculateTimeUntilExpiry = useCallback(() => {
        const expiryTime = getTokenExpiry();
        if (!expiryTime) return null;

        const now = Date.now();
        const timeLeft = expiryTime - now;

        return timeLeft > 0 ? timeLeft : 0;
    }, [getTokenExpiry]);

    // Check if warning should be shown
    const checkTokenExpiry = useCallback(() => {
        if (!isAuthenticated) {
            setIsWarningVisible(false);
            setTimeUntilExpiry(null);
            warningShownRef.current = false;
            return;
        }

        const timeLeft = calculateTimeUntilExpiry();

        if (timeLeft === null) {
            // No valid token found
            return;
        }

        setTimeUntilExpiry(timeLeft);

        // Convert warning threshold from minutes to milliseconds
        const warningThreshold = config.tokenExpiryWarningMinutes * 60 * 1000;

        if (timeLeft <= 0) {
            // Token has expired - force logout
            console.log('Token has expired, logging out user');
            logout().catch(error => console.error('Error during automatic logout:', error));
            setIsWarningVisible(false);
            warningShownRef.current = false;
        } else if (timeLeft <= warningThreshold && !warningShownRef.current) {
            // Token is about to expire - show warning
            console.log(`Token expires in ${Math.ceil(timeLeft / 60000)} minutes, showing warning`);
            setIsWarningVisible(true);
            warningShownRef.current = true;
        }
    }, [isAuthenticated, calculateTimeUntilExpiry, logout]);

    // Start monitoring token expiry
    const startMonitoring = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Convert check interval from minutes to milliseconds
        const checkInterval = config.tokenExpiryCheckIntervalMinutes * 60 * 1000;

        // Initial check
        checkTokenExpiry();

        // Set up periodic checks
        intervalRef.current = setInterval(checkTokenExpiry, checkInterval);

        // console.log(`Token expiry monitoring started - checking every ${config.tokenExpiryCheckIntervalMinutes} minute(s)`);
    }, [checkTokenExpiry]);

    // Stop monitoring
    const stopMonitoring = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsWarningVisible(false);
        setTimeUntilExpiry(null);
        warningShownRef.current = false;
        // console.log('Token expiry monitoring stopped');
    }, []);

    // Handle dismissing the warning
    const dismissWarning = useCallback(() => {
        setIsWarningVisible(false);
        // Don't reset warningShownRef here - we don't want to show the warning again
        // until the next token or the user refreshes
    }, []);

    // Handle extending session
    const extendSession = useCallback(async () => {
        try {
            const result = await refreshToken();

            if (result.success) {
                // Token refreshed successfully
                setIsWarningVisible(false);
                warningShownRef.current = false;

                // Immediately check the new token status
                setTimeout(checkTokenExpiry, 1000);

                return { success: true };
            } else {
                // Token refresh failed
                return { success: false, error: result.error };
            }
        } catch (error) {
            console.error('Error extending session:', error);
            return { success: false, error: error.message };
        }
    }, [refreshToken, checkTokenExpiry]);

    // Start monitoring when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            startMonitoring();
        } else {
            stopMonitoring();
        }

        return () => {
            stopMonitoring();
        };
    }, [isAuthenticated, startMonitoring, stopMonitoring]);

    // Format time for display
    const formatTimeLeft = useCallback((timeInMs) => {
        if (!timeInMs || timeInMs <= 0) return '0 minutes';

        const minutes = Math.ceil(timeInMs / 60000);
        if (minutes === 1) return '1 minute';
        return `${minutes} minutes`;
    }, []);

    return {
        isWarningVisible,
        timeUntilExpiry,
        formatTimeLeft: formatTimeLeft(timeUntilExpiry),
        dismissWarning,
        extendSession,
        checkTokenExpiry, // Expose for manual checks if needed
    };
};

export default useTokenExpiry;